import React, { useEffect } from "react";

import { AlertTriangle, ArrowLeft, X } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchOrganizationLevels } from "../../Store/Reducers/AdminSlice";
import {
  DisconnectReviewAccount,
  DisconnectSponsorAssessment,
} from "../../Store/Reducers/AssessmentSlice";
import { getDisconnectStatusLabel } from "../../Utils/Constants/disconnectStatusLabels";
import { utcTodayInputValue } from "../../Utils/Helper/assessmentDateUtils";
import {
  extractLevelName,
  getAccountLevelCompactLabel,
} from "../../Utils/Helper/levelUtils";

type OrganizationLevel = {
  level_code: string;
  name: string;
};

const AVATAR_COLORS = [
  "#7c3aed",
  "#059669",
  "#1d4ed8",
  "#d97706",
  "#be123c",
  "#0891b2",
];

const LEVEL_SCALE_COLORS = [
  "#94a3b8",
  "#f87171",
  "#fb923c",
  "#fbbf24",
  "#60a5fa",
  "#4ade80",
  "#a78bfa",
  "#34d399",
];

const LEVEL_BADGE_CLASSES: Record<number, string> = {
  0: "bg-slate-100 text-slate-700",
  1: "bg-red-50 text-red-700",
  2: "bg-orange-50 text-orange-700",
  3: "bg-yellow-50 text-yellow-800",
  4: "bg-sky-50 text-sky-700",
  5: "bg-emerald-50 text-emerald-700",
  6: "bg-violet-50 text-violet-700",
  7: "bg-indigo-50 text-indigo-700",
};

/** Match org level by numeric level from disconnect-review API. */
function findOrgLevel(
  levelNum: number,
  organizationLevels: OrganizationLevel[]
): OrganizationLevel | undefined {
  return organizationLevels.find(item => {
    const codeNum = Number(String(item.level_code).replace(/^L/i, ""));
    return Number.isFinite(codeNum) && codeNum === levelNum;
  });
}

function readSponsorLevel(sponsor: DisconnectSponsorAssessment): number | null {
  const record = sponsor as DisconnectSponsorAssessment &
    Record<string, unknown>;
  const value = record.level ?? record["level"];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readSponsorLevelLabel(
  sponsor: DisconnectSponsorAssessment
): string | null | undefined {
  return sponsor.levelLabel ?? null;
}

/** Display label using org levels API + disconnect-review level / levelLabel. */
function apiLevelDisplay(
  levelNum: number | null,
  levelLabel: string | null | undefined,
  organizationLevels: OrganizationLevel[]
): string {
  if (levelNum == null) {
    return levelLabel?.trim() || "Not submitted";
  }
  const orgLevel = findOrgLevel(levelNum, organizationLevels);
  return getAccountLevelCompactLabel(
    levelNum,
    orgLevel?.name ?? extractLevelName(levelLabel)
  );
}

export function avatarColor(name?: string | null): string {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i += 1) {
    hash = name!.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function initials(name?: string | null): string {
  return (name || "?")
    .split(" ")
    .map(part => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function disconnectLevelBadge(
  sponsor: DisconnectSponsorAssessment,
  organizationLevels: OrganizationLevel[]
) {
  const levelNum = readSponsorLevel(sponsor);
  const levelLabel = readSponsorLevelLabel(sponsor);
  const badgeClass =
    levelNum == null
      ? "bg-gray-100 text-gray-500"
      : (LEVEL_BADGE_CLASSES[levelNum] ?? "bg-gray-100 text-gray-700");

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass}`}
    >
      {apiLevelDisplay(levelNum, levelLabel, organizationLevels)}
    </span>
  );
}

function disconnectTierPill(tier: string, delta: number) {
  const isStrong = tier === "strong";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
        isStrong
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-amber-200 bg-amber-50 text-amber-700"
      }`}
    >
      {isStrong ? "Strong" : "Soft"} Disconnect · Δ{delta} level
      {delta !== 1 ? "s" : ""}
    </span>
  );
}

function levelPosition(level: number, maxLevel = 7) {
  return `${(level / maxLevel) * 100}%`;
}

function DisconnectLevelRangeBar({
  minLevel,
  medianLevel,
  maxLevel,
  eslFinalLevel,
  isEslOverridden,
  organizationLevels,
}: {
  minLevel: number;
  medianLevel: number;
  maxLevel: number;
  eslFinalLevel: number;
  isEslOverridden: boolean;
  organizationLevels: OrganizationLevel[];
}) {
  const maxScale = 7;
  const finalLevelName = apiLevelDisplay(
    eslFinalLevel,
    `Level ${eslFinalLevel}`,
    organizationLevels
  );
  const finalLabel = isEslOverridden
    ? `Final: ${finalLevelName} (overridden)`
    : `Final: ${finalLevelName}`;

  const baseMarkers = [
    {
      key: "min",
      level: minLevel,
      cls: "bg-red-500",
      label: `Min: ${apiLevelDisplay(minLevel, `Level ${minLevel}`, organizationLevels)}`,
      textCls: "text-red-600",
    },
    {
      key: "median",
      level: medianLevel,
      cls: "bg-amber-500",
      label: `Median: ${apiLevelDisplay(medianLevel, `Level ${medianLevel}`, organizationLevels)}`,
      textCls: "text-amber-600",
    },
    {
      key: "max",
      level: maxLevel,
      cls: "bg-purple-600",
      label: `Max: ${apiLevelDisplay(maxLevel, `Level ${maxLevel}`, organizationLevels)}`,
      textCls: "text-purple-600",
    },
    {
      key: "esl",
      level: eslFinalLevel,
      cls: "bg-green-600 ring-2 ring-white",
      label: finalLabel,
      textCls: "text-green-600",
      larger: true,
    },
  ];

  const markers = baseMarkers.map((marker, index) => {
    const overlapIndex = baseMarkers
      .slice(0, index)
      .filter(other => other.level === marker.level).length;
    return { ...marker, overlapIndex };
  });

  return (
    <div className="mt-2.5 rounded-lg border border-gray-200 bg-slate-50 p-3.5">
      <div className="mb-3 text-[10.5px] font-bold uppercase tracking-wider text-gray-500">
        Sponsor Level Distribution &amp; Final Level
      </div>
      <div className="relative mb-3.5">
        <div className="flex h-2.5 overflow-hidden rounded-md">
          {LEVEL_SCALE_COLORS.map((color, index) => (
            <div
              key={index}
              className="flex-1"
              style={{
                backgroundColor: `${color}33`,
                borderRight:
                  index < LEVEL_SCALE_COLORS.length - 1
                    ? `1px solid ${color}66`
                    : undefined,
              }}
            />
          ))}
        </div>
        {markers.map(marker => (
          <div
            key={marker.key}
            className="absolute top-1/2 z-10"
            style={{ left: levelPosition(marker.level, maxScale) }}
          >
            <div
              className={`rounded-full border-2 border-white shadow ${marker.cls} ${
                marker.larger ? "h-[18px] w-[18px]" : "h-4 w-4"
              }`}
              style={{
                transform: `translate(calc(-50% + ${marker.overlapIndex * 7}px), -50%)`,
              }}
            />
          </div>
        ))}
      </div>
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1">
        {markers.map(marker => (
          <div
            key={marker.key}
            className={`flex items-center gap-1 text-[11px] font-semibold ${marker.textCls}`}
          >
            <span
              className={`h-2 w-2 rounded-full ${marker.cls.split(" ")[0]}`}
            />
            {marker.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function DisconnectAccountCard({
  account,
  organizationLevels,
}: {
  account: DisconnectReviewAccount;
  organizationLevels: OrganizationLevel[];
}) {
  const sponsors = account.sponsorAssessments ?? [];

  return (
    <div className="mb-3 overflow-hidden rounded-lg border border-gray-200">
      <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-3.5 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900">
            {account.accountName}
          </div>
          {account.accountSector && (
            <div className="mt-0.5 text-[11px] text-gray-500">
              {account.accountSector}
            </div>
          )}
        </div>
        <div className="ml-auto shrink-0">
          {disconnectTierPill(account.deltaTier, account.delta)}
        </div>
      </div>
      <div className="p-3.5">
        <div className="space-y-2">
          {sponsors.map((sponsor, index) => (
            <div
              key={`${sponsor.executiveId || sponsor.name}-${index}`}
              className="rounded-lg border border-gray-200 p-2.5"
            >
              <div className="mb-1 flex items-center gap-2">
                <div
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10.5px] font-bold text-white"
                  style={{ backgroundColor: avatarColor(sponsor.name) }}
                >
                  {initials(sponsor.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900">
                    {sponsor.name}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {sponsor.title || sponsor.role || "Executive Sponsor"}
                  </div>
                </div>
                <div className="ml-auto shrink-0">
                  {disconnectLevelBadge(sponsor, organizationLevels)}
                </div>
              </div>
              {sponsor.rationale && (
                <p className="border-t border-gray-100 pt-2 text-xs leading-relaxed text-gray-600">
                  {sponsor.rationale}
                </p>
              )}
            </div>
          ))}
        </div>
        <DisconnectLevelRangeBar
          minLevel={account.minLevel}
          medianLevel={account.medianLevel}
          maxLevel={account.maxLevel}
          eslFinalLevel={account.eslFinalLevel}
          isEslOverridden={account.isEslOverridden}
          organizationLevels={organizationLevels}
        />
      </div>
    </div>
  );
}

export interface DisconnectTriggerModalProps {
  isOpen: boolean;
  selectedAccounts: DisconnectReviewAccount[];
  triggerDueDate: string;
  onDueDateChange: (value: string) => void;
  triggerContextNote: string;
  onContextNoteChange: (value: string) => void;
  defaultDueDateDays: number;
  triggerLoading: boolean;
  onBack?: () => void;
  onClose: () => void;
  onSubmit: () => void;
}

const DisconnectTriggerModal: React.FC<DisconnectTriggerModalProps> = ({
  isOpen,
  selectedAccounts,
  triggerDueDate,
  onDueDateChange,
  triggerContextNote,
  onContextNoteChange,
  defaultDueDateDays,
  triggerLoading,
  onBack,
  onClose,
  onSubmit,
}) => {
  const dispatch = useAppDispatch();
  const { organizationLevels } = useAppSelector((state: any) => state.admin);

  useEffect(() => {
    if (isOpen && organizationLevels.length === 0) {
      dispatch(fetchOrganizationLevels());
    }
  }, [dispatch, isOpen, organizationLevels.length]);

  if (!isOpen) return null;

  const accountCount = selectedAccounts.length;
  const orgLevels: OrganizationLevel[] = organizationLevels ?? [];

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/45 px-4 pb-4 pt-3">
      <div className="w-full max-w-[820px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-4 py-3">
          <div>
            <h3 className="text-[15px] font-bold text-gray-900">
              Trigger Disconnect Workflow
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-gray-500">
              Review assessment details and set resolution parameters for
              flagged accounts
            </p>
          </div>
          <div className="flex items-center gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                <ArrowLeft className="mr-1 h-3.5 w-3.5" />
                Back to Review
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="max-h-[calc(100vh-170px)] overflow-y-auto px-4 py-3.5">
          {selectedAccounts.map(account => (
            <DisconnectAccountCard
              key={`${account.assessmentId}_${account.accountId}`}
              account={account}
              organizationLevels={orgLevels}
            />
          ))}

          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="border-b border-gray-200 bg-white px-4 py-2.5">
              <span className="text-sm font-semibold text-gray-900">
                Resolution Parameters
              </span>
            </div>
            <div className="space-y-3.5 p-3.5">
              <div className="flex flex-wrap gap-4">
                <div className="min-w-[200px] flex-1">
                  <label className="mb-1.5 block text-xs font-semibold text-gray-700">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={triggerDueDate}
                    min={utcTodayInputValue()}
                    onChange={e => onDueDateChange(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="mt-1 text-[11px] text-gray-400">
                    Default: {defaultDueDateDays} business days from today
                  </p>
                </div>
                <div className="min-w-[280px] flex-[2]">
                  <label className="mb-1.5 block text-xs font-semibold text-gray-700">
                    Context / Note{" "}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={triggerContextNote}
                    onChange={e => onContextNoteChange(e.target.value)}
                    placeholder="e.g. Discussed in last week's QBR — key accounts for Q2 planning"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-2.5 text-xs leading-relaxed text-blue-800">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <div>
                  Triggering the workflow will notify{" "}
                  <strong>sponsors who assessed each account</strong> and all{" "}
                  <strong>ExecOps users</strong>. They will see raw assessments,
                  min/max/median levels, and the final level. Accounts move to{" "}
                  <strong>
                    {getDisconnectStatusLabel("pending_resolution")}
                  </strong>{" "}
                  status.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-4 py-2.5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-3.5 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!triggerDueDate || triggerLoading}
            className="inline-flex items-center rounded-md bg-purple-600 px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
          >
            {triggerLoading ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                Triggering…
              </>
            ) : (
              `Trigger Workflow for ${accountCount} Account${accountCount !== 1 ? "s" : ""} →`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisconnectTriggerModal;
